
import Vue from 'vue';
import { t } from '@/util-functions/language-utils';
import { generateId, showError } from '@/util-functions/misc-firestore-utils';
import { showConfirmation } from '@/util-functions/notice-utils';
import { hideLoading, showLoading } from '@/util-functions/loading-utils';
import { parse } from 'papaparse';
import { currFirestore, fbAuth, standardApiFetch } from '@/util-functions/initialization-utils';
import { endpoints, getEmptyCustomFormMode } from '@/constants';
import { eCustomFieldTypes, eModeType } from '@/enums';
import { saveMode } from '@/util-functions/firestore-mode-utils';
import { doc, updateDoc } from 'firebase/firestore';
import { getUserModeGatewayDoc } from '@/util-functions/user-utils';

export default Vue.extend({
  data(): {
    uploadedCsvFile: Blob | null;
    showProfileModeCreationFromCsvDialog: boolean;
    showProfileModeUpdateFromCsvDialog: boolean;
    showRedirectModeCreationFromCsvDialog: boolean;
  } {
    return {
      uploadedCsvFile: null,
      showProfileModeCreationFromCsvDialog: false,
      showProfileModeUpdateFromCsvDialog: false,
      showRedirectModeCreationFromCsvDialog: false,
    };
  },
  computed: {
    isAdmin(): boolean {
      return this.$store.state.isAdmin;
    },
    modes(): AnyMode[] {
      return this.$store.getters.sortedModes;
    },
  },
  methods: {
    onCreateProfileModesClick() {
      if (!this.isAdmin && !this.$store.state?.currUserModeGateway.hasPermanentPremium) {
        showError(t.thisIsAnEnterpriseTierFeature);
        return;
      }
      this.showProfileModeCreationFromCsvDialog = true;
      this.uploadedCsvFile = null; // Reset the file input
    },
    onUpdateProfileModesClick() {
      if (!this.isAdmin && !this.$store.state?.currUserModeGateway.hasPermanentPremium) {
        showError(t.thisIsAnEnterpriseTierFeature);
        return;
      }
      this.showProfileModeUpdateFromCsvDialog = true;
      this.uploadedCsvFile = null; // Reset the file input
    },
    onCreateRedirectModesClick() {
      if (!this.isAdmin && !this.$store.state?.currUserModeGateway.hasPermanentPremium) {
        showError(t.thisIsAnEnterpriseTierFeature);
        return;
      }
      this.showRedirectModeCreationFromCsvDialog = true;
      this.uploadedCsvFile = null; // Reset the file input
    },
    createFormsForProfileModes() {
      this.modes.forEach((mode) => {
        if (mode.type === eModeType.profile && (mode as ProfileMode).email && !(mode as ProfileMode).contactDetailsCustomFormModeId) {
          this.createFormForProfileMode(mode as ProfileMode);
        }
      });
    },
    createFormForProfileMode(mode: ProfileMode) {
      const docId = generateId();
      const customFormMode: CustomFormMode = {
        ...getEmptyCustomFormMode(),
        docId,
        linkId: '',
        themeId: '',
        index: 0,
        type: eModeType.customForm,
        name: t.contactDetailsFormTitle.supplant([mode.name]),
        displayName: t.shareYourInfo,
        message: '',
        requireName: true,
        requireEmail: true,
        requirePhoneNumber: false,
        customFields: [
          {
            id: generateId(),
            name: t.phone,
            type: eCustomFieldTypes.phoneNumber,
            description: '',
            choices: [],
            isRequired: false,
            isHidden: false,
          },
          {
            id: generateId(),
            name: t.message,
            type: eCustomFieldTypes.longText,
            description: '',
            choices: [],
            isRequired: false,
            isHidden: false,
          },
        ],
        // (combinedModeData.email.includes('@cryptoarena.com') && combinedModeData.email) ||
        emailForReceivingNotifications: mode.email,
      };
      saveMode(customFormMode).then(() => {
        // do update the profile mode to link to this form
        const publicUserModeGatewayDoc = getUserModeGatewayDoc();
        const modeDoc = doc(currFirestore, publicUserModeGatewayDoc.path, 'modes', mode.docId);
        updateDoc(modeDoc, {
          contactDetailsCustomFormModeId: docId,
          allowPeopleToSendTheirContactDetails: true,
        });
      });
    },
    updateProfileModes() {
      interface newProfileMode extends ProfileMode {
        defaultPassword: string;
        smallImageUrl?: string;
        smallImageFileName?: string;
        smallImageStoragePath?: string;
      }

      if (this.uploadedCsvFile) {
        const reader = new FileReader();

        reader.onload = (event) => {
          const csvString = event.target?.result as string;

          if (!csvString) {
            showError(`No CSV provided`);
            return;
          }

          const currUser = fbAuth.currentUser;

          if (!currUser) {
            showError(`No logged in user`, null, true);
            return;
          }

          showLoading();

          parse(csvString, {
            header: true,
            transform: function (s: string) {
              return s.trim();
            },
            skipEmptyLines: true,
            complete: (results: any) => {
              const profileModesToUpdate = results.data
                .filter((newProfileMode: newProfileMode) => {
                  return newProfileMode.displayName && newProfileMode.email;
                })
                .map((newProfileMode: newProfileMode) => {
                  // Remove any fields that are empty strings
                  if (newProfileMode.smallImageUrl && newProfileMode.smallImageFileName) {
                    newProfileMode.images = [
                      {
                        id: '',
                        smallImageUrl: newProfileMode.smallImageUrl,
                        smallImageFileName: newProfileMode.smallImageFileName,
                        smallImageStoragePath: newProfileMode.smallImageStoragePath,
                      },
                    ];
                  }
                  return newProfileMode;
                });

              if (profileModesToUpdate.length === 0) {
                showError(t.csvIsMalformed);
                hideLoading();
                return;
              }

              currUser
                .getIdToken(/* forceRefresh */ true)
                .then((idToken) => {
                  standardApiFetch(endpoints.updateMultipleProfileModes, {
                    profileModesToUpdate,
                    userId: this.$store.state.userId,
                    idToken,
                  })
                    .then(async () => {
                      if (profileModesToUpdate.length !== results.data.length) {
                        showError(t.someEntriesWereSkipped);
                      }

                      this.showProfileModeUpdateFromCsvDialog = false;

                      showConfirmation(t.profileModesSuccessfullyUpdated);

                      hideLoading(true);
                    })
                    .catch((error) => {
                      hideLoading();
                      showError(`Could not update the profile modes.`, error, true);
                    });
                })
                .catch((error) => {
                  hideLoading();
                  showError(`Could not get user token to make request`, error, true);
                });
            },
          });
        };
        reader.readAsText(this.uploadedCsvFile);
      } // Future feature: Update existing profile modes from CSV
    },
    createProfileModes() {
      interface newProfileMode extends ProfileMode {
        defaultPassword: string;
        smallImageUrl?: string;
        smallImageFileName?: string;
        smallImageStoragePath?: string;
      }

      if (this.uploadedCsvFile) {
        const reader = new FileReader();

        reader.onload = (event) => {
          const csvString = event.target?.result as string;

          if (!csvString) {
            showError(`No CSV provided`);
            return;
          }

          const currUser = fbAuth.currentUser;

          if (!currUser) {
            showError(`No logged in user`, null, true);
            return;
          }

          showLoading();

          parse(csvString, {
            header: true,
            transform: function (s: string) {
              return s.trim();
            },
            skipEmptyLines: true,
            complete: (results: any) => {
              const profileModesToCreate = results.data
                .filter((newProfileMode: newProfileMode) => {
                  return newProfileMode.displayName && newProfileMode.email;
                })
                .map((newProfileMode: newProfileMode) => {
                  // Remove any fields that are empty strings
                  if (newProfileMode.smallImageUrl && newProfileMode.smallImageFileName) {
                    newProfileMode.images = [
                      {
                        id: '',
                        smallImageUrl: newProfileMode.smallImageUrl,
                        smallImageFileName: newProfileMode.smallImageFileName,
                        smallImageStoragePath: newProfileMode.smallImageStoragePath,
                      },
                    ];
                  }
                  return newProfileMode;
                });

              if (profileModesToCreate.length === 0) {
                showError(t.csvIsMalformed);
                hideLoading();
                return;
              }

              currUser
                .getIdToken(/* forceRefresh */ true)
                .then((idToken) => {
                  standardApiFetch(endpoints.createMultipleProfileModes, {
                    profileModesToCreate,
                    userId: this.$store.state.userId,
                    idToken,
                  })
                    .then(async () => {
                      if (profileModesToCreate.length !== results.data.length) {
                        showError(t.someEntriesWereSkipped);
                      }

                      this.showProfileModeCreationFromCsvDialog = false;

                      showConfirmation(t.profileModesSuccessfullyCreated);

                      hideLoading(true);
                    })
                    .catch((error) => {
                      hideLoading();
                      showError(`Could not create the profile modes.`, error, true);
                    });
                })
                .catch((error) => {
                  hideLoading();
                  showError(`Could not get user token to make request`, error, true);
                });
            },
          });
        };
        reader.readAsText(this.uploadedCsvFile);
      }
    },
    createRedirectModes() {
      if (this.uploadedCsvFile) {
        const reader = new FileReader();

        reader.onload = (event) => {
          const csvString = event.target?.result as string;

          if (!csvString) {
            showError(`No CSV provided`);
            return;
          }

          const currUser = fbAuth.currentUser;

          if (!currUser) {
            showError(`No logged in user`, null, true);
            return;
          }

          showLoading();

          parse(csvString, {
            header: true,
            transform: function (s: string) {
              return s.trim();
            },
            skipEmptyLines: true,
            complete: (results: any) => {
              const redirectModesToCreate = results.data.filter((newRedirectMode: UrlRedirectMode) => {
                return newRedirectMode.displayName && newRedirectMode.redirectUrl;
              });

              if (redirectModesToCreate.length === 0) {
                showError(t.csvIsMalformed);
                hideLoading();
                return;
              }

              currUser
                .getIdToken(/* forceRefresh */ true)
                .then((idToken) => {
                  standardApiFetch(endpoints.createMultipleRedirectModes, {
                    redirectModesToCreate,
                    userId: this.$store.state.userId,
                    idToken,
                  })
                    .then(async () => {
                      if (redirectModesToCreate.length !== results.data.length) {
                        showError(t.someEntriesWereSkipped);
                      }

                      this.showRedirectModeCreationFromCsvDialog = false;

                      showConfirmation(t.redirectModesSuccessfullyCreated);

                      hideLoading(true);
                    })
                    .catch((error) => {
                      hideLoading();
                      showError(`Could not create the accounts.`, error, true);
                    });
                })
                .catch((error) => {
                  hideLoading();
                  showError(`Could not get user token to make request`, error, true);
                });
            },
          });
        };
        reader.readAsText(this.uploadedCsvFile);
      }
    },
  },
});
